import { LogOut } from "lucide-react";
import { getAccessToken, clearLocalStorage } from "@/lib/storeManager";
import DarkThemeWidget from "@sd/components/DarkThemeWidget";

/*
// ─── Full future marketing copy (not all live yet) ───────────────────────────
const featuresPlanned = [
  {
    tag: "CAPTURE",
    title: "Screenshot · Console · Network · Errors · Breadcrumbs",
    desc: "Every report includes a viewport screenshot, last 200 console lines, last 50 network requests, all unhandled errors with stack traces, and the user's last 50 actions — selectors only, never values.",
  },
  {
    tag: "HARDENING",
    title: "PII auto-redacted before transmission",
    desc: "Emails, phones, SSNs, Luhn-validated credit cards, JWTs, AWS/GitHub/Stripe/OpenAI keys, Bearer tokens. Sensitive form fields masked before screenshot. Recursive object redaction with circular-reference guards.",
  },
  {
    tag: "RELIABILITY",
    title: "Offline queue · Exponential backoff · Rate limit",
    desc: "Reports survive page closes via IndexedDB. Network failures retry with full-jitter backoff. Token-bucket rate limiter prevents runaway loops. Hard 2 MB payload cap with progressive degradation.",
  },
  {
    tag: "AI",
    title: "Triage before ticket",
    desc: "As the user types, an AI suggestion is fetched from your RAG endpoint. If confidence ≥ 70%, it's shown inline with sources. Many issues never become tickets — that's the deflection loop.",
  },
];
*/

/** Live copy — honest claims only (Option A). */
const features = [
  {
    tag: "CAPTURE",
    title: "Screenshot · Attachments · Screen recording",
    desc: "Add a viewport or screen capture, upload attachments, or record your screen — attach what helps explain the issue when you submit a ticket.",
  },
  {
    tag: "HARDENING",
    title: "Input sanitized before save",
    desc: "Subject and description are cleaned before the ticket is stored — HTML tags and unsafe characters are stripped so submissions stay safe and readable.",
  },
  {
    tag: "RELIABILITY",
    title: "Guided flow · Validated uploads",
    desc: "A simple two-step widget walks you through requester details and ticket fields. Image attachments follow allowed types and size checks so submissions stay clear and usable.",
  },
  {
    tag: "SUPPORT",
    title: "Ticket to your help desk",
    desc: "Subject, category, priority, and description go to the Service Desk team — so agents can review, assign, and follow up on your request.",
  },
];

export default function UserPortalHome() {
  const isLoggedIn = !!getAccessToken();
  const handleSignOut = () => {
    clearLocalStorage();
    window.location.href = "/sd/login";
  };

  return (
    <div className="min-h-screen bg-white text-gray-900 font-sans">
      {/* ── Navbar ── */}
      <nav className="sticky top-0 z-40 bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between shadow-sm">
        <span className="text-xs font-bold tracking-[0.25em] text-gray-500 uppercase">
          ServiceDesk · Portal
        </span>
        {isLoggedIn && (
          <div className="flex items-center gap-3">
            <button
              onClick={handleSignOut}
              className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 transition-colors"
            >
              <LogOut size={15} /> Sign out
            </button>
          </div>
        )}
      </nav>

      {/* ── Hero ── */}
      <section className="max-w-5xl mx-auto px-6 pt-20 pb-12">
        <p className="text-xs font-bold tracking-[0.25em] text-gray-400 uppercase mb-6">
          SYSTEMDESK · V2.1.0
        </p>
        <h1 className="text-5xl font-extrabold leading-tight text-gray-900 mb-6">
          The widget that{" "}
          <span className="text-orange-500">captures your issue.</span>
          <br />
          Then asks fewer questions.
        </h1>
        <p className="text-gray-500 text-lg max-w-2xl leading-relaxed">
          Click the support button bottom-right. Type a problem. Add screenshots
          or attachments if you need to — and when you submit, useful context
          for the support team is already included.
        </p>
      </section>

      {/* ── Feature cards ── */}
      <section className="max-w-5xl mx-auto px-6 pb-24 grid grid-cols-1 sm:grid-cols-2 gap-4">
        {features.map(f => (
          <div
            key={f.tag}
            className="bg-gray-50 border border-gray-200 rounded-xl p-6 hover:shadow-md transition-shadow"
          >
            <p className="text-xs font-bold tracking-widest text-orange-500 mb-3">
              {f.tag}
            </p>
            <p className="font-bold text-gray-900 mb-2">{f.title}</p>
            <p className="text-gray-500 text-sm leading-relaxed">{f.desc}</p>
          </div>
        ))}
      </section>

      {/* ── Dark Theme Widget ── */}
      <DarkThemeWidget />
    </div>
  );
}
